package fr.frux.feature.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.windowInsetsTopHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Download
import androidx.compose.material.icons.outlined.Eco
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material.icons.outlined.Feedback
import androidx.compose.material.icons.outlined.NotificationsNone
import androidx.compose.material.icons.outlined.OndemandVideo
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.PrivacyTip
import androidx.compose.material.icons.outlined.Settings
import androidx.compose.material.icons.outlined.ShoppingBasket
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material.icons.outlined.WorkspacePremium
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.layout.layout
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.profile_favorites_plural
import fr.frux.app.resources.profile_favorites_singular
import fr.frux.app.resources.profile_harvests_organized_plural
import fr.frux.app.resources.profile_harvests_organized_singular
import fr.frux.app.resources.profile_logout
import fr.frux.app.resources.profile_logout_all
import fr.frux.app.resources.profile_member_since
import fr.frux.app.resources.profile_row_activity
import fr.frux.app.resources.profile_row_activity_subtitle
import fr.frux.app.resources.profile_row_ad_choices
import fr.frux.app.resources.profile_row_ad_choices_subtitle
import fr.frux.app.resources.profile_row_blocked_users
import fr.frux.app.resources.profile_row_blocked_users_subtitle
import fr.frux.app.resources.profile_row_delete_account
import fr.frux.app.resources.profile_row_export
import fr.frux.app.resources.profile_row_export_subtitle
import fr.frux.app.resources.profile_row_feedback
import fr.frux.app.resources.profile_row_feedback_subtitle
import fr.frux.app.resources.profile_row_my_trees
import fr.frux.app.resources.profile_row_my_trees_subtitle
import fr.frux.app.resources.profile_row_premium
import fr.frux.app.resources.profile_row_premium_subtitle
import fr.frux.app.resources.profile_row_rewarded_ad
import fr.frux.app.resources.profile_row_rewarded_ad_loading
import fr.frux.app.resources.profile_row_rewarded_ad_validating
import fr.frux.app.resources.profile_row_settings
import fr.frux.app.resources.profile_section_account
import fr.frux.app.resources.profile_section_activity
import fr.frux.app.resources.profile_title
import fr.frux.app.resources.profile_trees_shared_plural
import fr.frux.app.resources.profile_trees_shared_singular
import fr.frux.app.resources.profile_version
import fr.frux.core.common.config.appVersion
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.layout.FruxLayoutDefaults
import fr.frux.core.designsystem.system.StatusBarIconsEffect
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.userprofile.formatMemberSince
import fr.frux.shared.domain.model.User
import fr.frux.shared.domain.model.UserProfile
import org.jetbrains.compose.resources.stringResource

@Composable
private fun memberSinceLabel(user: User): String =
    stringResource(Res.string.profile_member_since, formatMemberSince(user.createdAt))

/** Bâtit les données des tuiles de compteurs à partir des contributions et des favoris (#442). */
@Composable
private fun contributionTiles(
    contributions: UserProfile,
    favoritesCount: Int?,
): List<ContributionTileData> {
    val tiles =
        mutableListOf(
            ContributionTileData(
                icon = Icons.Outlined.Park,
                count = contributions.treesCount,
                label =
                    if (contributions.treesCount > 1) {
                        stringResource(Res.string.profile_trees_shared_plural)
                    } else {
                        stringResource(Res.string.profile_trees_shared_singular)
                    },
                iconContainerColor = MaterialTheme.colorScheme.primaryContainer,
                iconTint = MaterialTheme.colorScheme.primary,
            ),
            ContributionTileData(
                icon = Icons.Outlined.ShoppingBasket,
                count = contributions.harvestsCount,
                label =
                    if (contributions.harvestsCount > 1) {
                        stringResource(Res.string.profile_harvests_organized_plural)
                    } else {
                        stringResource(Res.string.profile_harvests_organized_singular)
                    },
                iconContainerColor = MaterialTheme.colorScheme.tertiaryContainer,
                iconTint = MaterialTheme.colorScheme.onTertiaryContainer,
            ),
        )
    if (favoritesCount != null) {
        tiles +=
            ContributionTileData(
                icon = Icons.Outlined.FavoriteBorder,
                count = favoritesCount,
                label =
                    if (favoritesCount > 1) {
                        stringResource(Res.string.profile_favorites_plural)
                    } else {
                        stringResource(Res.string.profile_favorites_singular)
                    },
                iconContainerColor = MaterialTheme.colorScheme.errorContainer,
                iconTint = MaterialTheme.colorScheme.error,
            )
    }
    return tiles
}

/** Lignes de la section « Mon activité » (#442), communes aux deux dispositions. */
@Composable
private fun activityRowItems(
    unreadNotifications: Int,
    onOpenNotifications: () -> Unit,
    onOpenMyTrees: () -> Unit,
): List<ProfileRowItem> =
    listOf(
        ProfileRowItem(
            icon = Icons.Outlined.NotificationsNone,
            title = stringResource(Res.string.profile_row_activity),
            subtitle = stringResource(Res.string.profile_row_activity_subtitle),
            badgeCount = unreadNotifications,
            iconContainerColor = MaterialTheme.colorScheme.primaryContainer,
            iconTint = MaterialTheme.colorScheme.primary,
            onClick = onOpenNotifications,
        ),
        ProfileRowItem(
            icon = Icons.Outlined.Park,
            title = stringResource(Res.string.profile_row_my_trees),
            subtitle = stringResource(Res.string.profile_row_my_trees_subtitle),
            iconContainerColor = MaterialTheme.colorScheme.primaryContainer,
            iconTint = MaterialTheme.colorScheme.primary,
            onClick = onOpenMyTrees,
        ),
    )

/** Lignes de la section « Compte » (#442), communes aux deux dispositions. */
@Composable
private fun accountRowItems(
    isExporting: Boolean,
    onOpenSettings: () -> Unit,
    onOpenFeedback: () -> Unit,
    onOpenPremium: () -> Unit,
    onOpenBlockedUsers: () -> Unit,
    onExport: () -> Unit,
    onOpenAdChoices: (() -> Unit)?,
    onWatchRewarded: (() -> Unit)?,
    rewardedLoading: Boolean,
    rewardedValidating: Boolean,
): List<ProfileRowItem> {
    val containerColor = MaterialTheme.colorScheme.surfaceVariant
    val tint = MaterialTheme.colorScheme.onSurface
    val adChoices =
        onOpenAdChoices?.let {
            // Choix publicitaires (ADR 0030, #527) : seulement quand UMP exige d'y donner accès,
            // donc jamais sur le web, sur desktop, ni pour un compte sans publicité.
            ProfileRowItem(
                icon = Icons.Outlined.PrivacyTip,
                title = stringResource(Res.string.profile_row_ad_choices),
                subtitle = stringResource(Res.string.profile_row_ad_choices_subtitle),
                iconContainerColor = containerColor,
                iconTint = tint,
                onClick = it,
            )
        }
    val rewarded =
        onWatchRewarded?.let {
            // Vidéo récompensée (#531) : seulement quand la publicité est active pour ce compte.
            ProfileRowItem(
                icon = Icons.Outlined.OndemandVideo,
                title =
                    stringResource(
                        when {
                            rewardedLoading -> Res.string.profile_row_rewarded_ad_loading
                            rewardedValidating -> Res.string.profile_row_rewarded_ad_validating
                            else -> Res.string.profile_row_rewarded_ad
                        },
                    ),
                isLoading = rewardedLoading,
                enabled = !rewardedValidating,
                iconContainerColor = containerColor,
                iconTint = tint,
                onClick = it,
            )
        }
    return listOfNotNull(
        ProfileRowItem(
            icon = Icons.Outlined.Settings,
            title = stringResource(Res.string.profile_row_settings),
            iconContainerColor = containerColor,
            iconTint = tint,
            onClick = onOpenSettings,
        ),
        ProfileRowItem(
            icon = Icons.Outlined.WorkspacePremium,
            title = stringResource(Res.string.profile_row_premium),
            subtitle = stringResource(Res.string.profile_row_premium_subtitle),
            iconContainerColor = containerColor,
            iconTint = tint,
            onClick = onOpenPremium,
        ),
        ProfileRowItem(
            icon = Icons.Outlined.Feedback,
            title = stringResource(Res.string.profile_row_feedback),
            subtitle = stringResource(Res.string.profile_row_feedback_subtitle),
            iconContainerColor = containerColor,
            iconTint = tint,
            onClick = onOpenFeedback,
        ),
        ProfileRowItem(
            icon = Icons.Outlined.VisibilityOff,
            title = stringResource(Res.string.profile_row_blocked_users),
            subtitle = stringResource(Res.string.profile_row_blocked_users_subtitle),
            iconContainerColor = containerColor,
            iconTint = tint,
            onClick = onOpenBlockedUsers,
        ),
        adChoices,
        rewarded,
        ProfileRowItem(
            icon = Icons.Outlined.Download,
            title = stringResource(Res.string.profile_row_export),
            subtitle = stringResource(Res.string.profile_row_export_subtitle),
            isLoading = isExporting,
            iconContainerColor = containerColor,
            iconTint = tint,
            onClick = onExport,
        ),
    )
}

/** Identité (avatar, pseudo, email, ligne « cueille depuis ») — commune au bandeau et à la carte grand écran. */
@Composable
private fun ProfileIdentityBlock(
    user: User,
    contentColor: Color,
    subtleColor: Color,
    avatarBackground: Color,
    avatarContent: Color,
    modifier: Modifier = Modifier,
    avatarSize: Dp = 72.dp,
) {
    Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically) {
        Surface(
            modifier = Modifier.size(avatarSize),
            shape = CircleShape,
            color = avatarBackground,
        ) {
            Box(contentAlignment = Alignment.Center) {
                Text(
                    text = user.username.take(1).uppercase(),
                    style = MaterialTheme.typography.headlineMedium,
                    color = avatarContent,
                )
            }
        }
        Spacer(modifier = Modifier.width(FruxSpacing.md))
        Column {
            Text(
                text = user.username,
                style = MaterialTheme.typography.headlineSmall,
                color = contentColor,
            )
            Text(
                text = user.email,
                style = MaterialTheme.typography.bodyMedium,
                color = subtleColor,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = Icons.Outlined.Eco,
                    contentDescription = null,
                    tint = subtleColor,
                    modifier = Modifier.size(14.dp),
                )
                Spacer(modifier = Modifier.width(FruxSpacing.xs))
                Text(
                    text = memberSinceLabel(user),
                    style = MaterialTheme.typography.bodySmall,
                    color = subtleColor,
                )
            }
        }
    }
}

/** Couleurs du bandeau mobile : fond primary (clair) ou primaryContainer (sombre), et son contenu. */
private data class BannerColors(
    val background: Color,
    val content: Color,
)

@Composable
private fun bannerColors(): BannerColors {
    // Lu sur le thème appliqué et non sur le système : FruxTheme peut être forcé (tests, préférence).
    val darkTheme = MaterialTheme.colorScheme.background.luminance() < 0.5f
    return if (darkTheme) {
        BannerColors(MaterialTheme.colorScheme.primaryContainer, MaterialTheme.colorScheme.onPrimaryContainer)
    } else {
        BannerColors(MaterialTheme.colorScheme.primary, MaterialTheme.colorScheme.onPrimary)
    }
}

/**
 * Bande fixe de la hauteur de la barre d'état, peinte aux couleurs du bandeau (#595) : le bandeau
 * scrolle dessous, la zone des icônes système garde le même fond quel que soit le défilement.
 * Les icônes suivent la teinte de ce fond, pas celle du thème.
 */
@Composable
private fun StatusBarScrim(colors: BannerColors) {
    StatusBarIconsEffect(lightIcons = colors.background.luminance() < 0.5f)
    Spacer(
        modifier =
            Modifier
                .fillMaxWidth()
                .windowInsetsTopHeight(WindowInsets.statusBars)
                .background(colors.background),
    )
}

/** Bandeau plein écran (mobile / Compact), sous [StatusBarScrim]. */
@Composable
private fun ProfileBanner(
    user: User,
    colors: BannerColors,
    modifier: Modifier = Modifier,
) {
    val background = colors.background
    val onBackground = colors.content
    Surface(modifier = modifier.fillMaxWidth(), color = background) {
        Box {
            // Feuille décorative en filigrane : purement ornementale, aucune sémantique (#442).
            Icon(
                imageVector = Icons.Outlined.Eco,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.inversePrimary,
                modifier =
                    Modifier
                        .size(200.dp)
                        .align(Alignment.TopEnd)
                        .padding(top = 0.dp)
                        .alpha(0.18f),
            )
            Column(
                modifier =
                    Modifier.padding(
                        start = FruxSpacing.md,
                        end = FruxSpacing.md,
                        top = FruxSpacing.lg,
                        bottom = 56.dp,
                    ),
            ) {
                Text(
                    text = stringResource(Res.string.profile_title),
                    style = MaterialTheme.typography.headlineSmall.copy(fontWeight = FontWeight.Bold),
                    color = onBackground,
                )
                Spacer(modifier = Modifier.height(FruxSpacing.md))
                ProfileIdentityBlock(
                    user = user,
                    contentColor = onBackground,
                    subtleColor = onBackground.copy(alpha = 0.75f),
                    avatarBackground = MaterialTheme.colorScheme.tertiaryContainer,
                    avatarContent = MaterialTheme.colorScheme.onTertiaryContainer,
                )
            }
        }
    }
}

/** Disposition mobile / Compact (ordre du mock Main.dc.html, #442). */
@Composable
internal fun ProfileMobileContent(
    user: User,
    state: ProfileState,
    viewModel: ProfileViewModel,
    onOpenSettings: () -> Unit,
    onOpenMyTrees: () -> Unit,
    onOpenNotifications: () -> Unit,
    onOpenFeedback: () -> Unit,
    onOpenPremium: () -> Unit,
    onOpenBlockedUsers: () -> Unit,
    onAddTree: () -> Unit,
    onOpenAdChoices: (() -> Unit)?,
    modifier: Modifier = Modifier,
) {
    val colors = bannerColors()
    Column(
        modifier =
            modifier
                .fillMaxSize()
                .padding(bottom = LocalBottomNavPadding.current),
    ) {
        StatusBarScrim(colors)
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(rememberScrollState()),
        ) {
            ProfileBanner(user = user, colors = colors)

            Column(modifier = Modifier.fillMaxWidth().padding(horizontal = FruxSpacing.md)) {
                // Le chevauchement sur le bandeau reste hors de l'animation : c'est la carte qui
                // se déplie à sa place, pas la place elle-même qui bouge.
                ProfileReveal(
                    value = state.contributions,
                    modifier = Modifier.overlapAbove(TILES_OVERLAP),
                ) { contributions ->
                    Column {
                        ProfileContributionTiles(
                            items = contributionTiles(contributions, state.favoritesCount),
                        )
                        Spacer(modifier = Modifier.height(FruxSpacing.md))
                    }
                }

                ProfileReveal(value = state.nudge) { nudge ->
                    Column {
                        ContributionNudgeCard(
                            nudge = nudge,
                            onPrimary = if (nudge == ContributionNudge.FIRST_TREE) onAddTree else onOpenMyTrees,
                            onDismiss = viewModel::dismissNudge,
                        )
                        Spacer(modifier = Modifier.height(FruxSpacing.lg))
                    }
                }

                ProfileSectionHeader(stringResource(Res.string.profile_section_activity))
                Spacer(modifier = Modifier.height(FruxSpacing.sm))
                ProfileRowsCard(
                    activityRowItems(state.unreadNotifications, onOpenNotifications, onOpenMyTrees),
                )

                if (state.supportVisible) {
                    Spacer(modifier = Modifier.height(FruxSpacing.lg))
                    ProfileSupportCard(onClick = viewModel::onSupportClicked)
                }

                Spacer(modifier = Modifier.height(FruxSpacing.lg))
                ProfileSectionHeader(stringResource(Res.string.profile_section_account))
                Spacer(modifier = Modifier.height(FruxSpacing.sm))
                ProfileRowsCard(
                    accountRowItems(
                        isExporting = state.isExporting,
                        onOpenSettings = onOpenSettings,
                        onOpenFeedback = onOpenFeedback,
                        onOpenPremium = onOpenPremium,
                        onOpenBlockedUsers = onOpenBlockedUsers,
                        onExport = { if (!state.isExporting) viewModel.exportAccount() },
                        onOpenAdChoices = onOpenAdChoices,
                        onWatchRewarded =
                            if (state.rewardedOfferVisible ||
                                state.rewardedLoading ||
                                state.rewardedValidating
                            ) {
                                viewModel::watchRewardedAd
                            } else {
                                null
                            },
                        rewardedLoading = state.rewardedLoading,
                        rewardedValidating = state.rewardedValidating,
                    ),
                )

                Spacer(modifier = Modifier.height(FruxSpacing.xl))
                ProfileFooter(viewModel = viewModel, horizontalArrangement = Arrangement.Center)
                Spacer(modifier = Modifier.height(FruxSpacing.md))
            }
        }
    }
}

/** Pied commun (bouton de déconnexion + liens + version), factorisé entre les deux dispositions. */
@Composable
private fun ProfileFooter(
    viewModel: ProfileViewModel,
    horizontalArrangement: Arrangement.Horizontal,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
        ProfileLogoutButton(
            onClick = viewModel::logout,
            modifier = Modifier.fillMaxWidth(),
            label = stringResource(Res.string.profile_logout),
        )
        Spacer(modifier = Modifier.height(FruxSpacing.xs))
        // Sans séparateur entre les deux liens : à largeur de téléphone la ligne se replie, et un
        // point médian resterait orphelin en bout de première ligne.
        FlowRow(
            horizontalArrangement = horizontalArrangement,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.fillMaxWidth(),
        ) {
            ProfileFooterLink(
                text = stringResource(Res.string.profile_logout_all),
                onClick = viewModel::requestLogoutAllConfirmation,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            ProfileFooterLink(
                text = stringResource(Res.string.profile_row_delete_account),
                onClick = viewModel::startAccountDeletion,
                color = MaterialTheme.colorScheme.error,
            )
        }
        Spacer(modifier = Modifier.height(FruxSpacing.xs))
        Text(
            text = stringResource(Res.string.profile_version, appVersion()),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/**
 * Disposition grand écran (mock Web.dc.html) : à côté du rail, le profil est une page à carte
 * identité arrondie sur fond clair, jamais le bandeau bord à bord du mobile (qui, posé dans une
 * colonne, s'arrêtait au rail et laissait la barre d'état mi-teinte). Deux colonnes dès que la
 * zone le permet ([twoColumns]), sinon les mêmes blocs empilés dans une colonne.
 */
@Composable
internal fun ProfileExpandedContent(
    user: User,
    state: ProfileState,
    viewModel: ProfileViewModel,
    onOpenSettings: () -> Unit,
    onOpenMyTrees: () -> Unit,
    onOpenNotifications: () -> Unit,
    onOpenFeedback: () -> Unit,
    onOpenPremium: () -> Unit,
    onOpenBlockedUsers: () -> Unit,
    onAddTree: () -> Unit,
    onOpenAdChoices: (() -> Unit)?,
    twoColumns: Boolean,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxSize()
                .windowInsetsPadding(WindowInsets.statusBars)
                .verticalScroll(rememberScrollState())
                .padding(horizontal = FruxSpacing.xl, vertical = FruxSpacing.lg),
    ) {
        Text(
            text = stringResource(Res.string.profile_title),
            style = MaterialTheme.typography.headlineLarge,
            color = MaterialTheme.colorScheme.primary,
        )
        Spacer(modifier = Modifier.height(FruxSpacing.lg))
        val identity: @Composable ColumnScope.() -> Unit = {
            ProfileIdentityCard(
                user = user,
                contributions = state.contributions,
                favoritesCount = state.favoritesCount,
            )
            if (state.supportVisible) {
                Spacer(modifier = Modifier.height(FruxSpacing.md))
                ProfileSupportCard(onClick = viewModel::onSupportClicked)
            }
        }
        val sections: @Composable ColumnScope.() -> Unit = {
            ProfileExpandedSections(
                state = state,
                viewModel = viewModel,
                onOpenSettings = onOpenSettings,
                onOpenMyTrees = onOpenMyTrees,
                onOpenNotifications = onOpenNotifications,
                onOpenFeedback = onOpenFeedback,
                onOpenPremium = onOpenPremium,
                onOpenBlockedUsers = onOpenBlockedUsers,
                onAddTree = onAddTree,
                onOpenAdChoices = onOpenAdChoices,
            )
        }
        if (twoColumns) {
            Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.lg)) {
                Column(modifier = Modifier.widthIn(max = 380.dp), content = identity)
                Column(modifier = Modifier.weight(1f), content = sections)
            }
        } else {
            Column(modifier = Modifier.widthIn(max = FruxLayoutDefaults.FullZoneMaxWidth)) {
                identity()
                Spacer(modifier = Modifier.height(FruxSpacing.lg))
                sections()
            }
        }
    }
}

/** Nudge, activité, compte et pied : la colonne de droite du grand écran, ou la suite de la pile. */
@Composable
private fun ColumnScope.ProfileExpandedSections(
    state: ProfileState,
    viewModel: ProfileViewModel,
    onOpenSettings: () -> Unit,
    onOpenMyTrees: () -> Unit,
    onOpenNotifications: () -> Unit,
    onOpenFeedback: () -> Unit,
    onOpenPremium: () -> Unit,
    onOpenBlockedUsers: () -> Unit,
    onAddTree: () -> Unit,
    onOpenAdChoices: (() -> Unit)?,
) {
    ProfileReveal(value = state.nudge) { nudge ->
        Column {
            ContributionNudgeCard(
                nudge = nudge,
                onPrimary = if (nudge == ContributionNudge.FIRST_TREE) onAddTree else onOpenMyTrees,
                onDismiss = viewModel::dismissNudge,
            )
            Spacer(modifier = Modifier.height(FruxSpacing.lg))
        }
    }

    ProfileSectionHeader(stringResource(Res.string.profile_section_activity))
    Spacer(modifier = Modifier.height(FruxSpacing.sm))
    val activityItems = activityRowItems(state.unreadNotifications, onOpenNotifications, onOpenMyTrees)
    Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        activityItems.forEach { item ->
            ProfileActivityTile(item = item, modifier = Modifier.weight(1f))
        }
    }

    Spacer(modifier = Modifier.height(FruxSpacing.lg))
    ProfileSectionHeader(stringResource(Res.string.profile_section_account))
    Spacer(modifier = Modifier.height(FruxSpacing.sm))
    ProfileRowsCard(
        accountRowItems(
            isExporting = state.isExporting,
            onOpenSettings = onOpenSettings,
            onOpenFeedback = onOpenFeedback,
            onOpenPremium = onOpenPremium,
            onOpenBlockedUsers = onOpenBlockedUsers,
            onExport = { if (!state.isExporting) viewModel.exportAccount() },
            onOpenAdChoices = onOpenAdChoices,
            onWatchRewarded =
                if (state.rewardedOfferVisible ||
                    state.rewardedLoading ||
                    state.rewardedValidating
                ) {
                    viewModel::watchRewardedAd
                } else {
                    null
                },
            rewardedLoading = state.rewardedLoading,
            rewardedValidating = state.rewardedValidating,
        ),
    )

    Spacer(modifier = Modifier.height(FruxSpacing.lg))
    ProfileFooter(viewModel = viewModel, horizontalArrangement = Arrangement.Start)
}

/** Carte identité du grand écran : fond primary, avatar + tuiles translucides intégrées. */
@Composable
private fun ProfileIdentityCard(
    user: User,
    contributions: UserProfile?,
    favoritesCount: Int?,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.extraLarge,
        color = MaterialTheme.colorScheme.primary,
    ) {
        Box {
            Icon(
                imageVector = Icons.Outlined.Eco,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.inversePrimary,
                modifier =
                    Modifier
                        .size(220.dp)
                        .align(Alignment.TopEnd)
                        .alpha(0.18f),
            )
            Column(modifier = Modifier.padding(FruxSpacing.lg)) {
                ProfileIdentityBlock(
                    user = user,
                    contentColor = MaterialTheme.colorScheme.onPrimary,
                    subtleColor = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.75f),
                    avatarBackground = MaterialTheme.colorScheme.tertiaryContainer,
                    avatarContent = MaterialTheme.colorScheme.onTertiaryContainer,
                    avatarSize = 84.dp,
                )
                ProfileReveal(value = contributions) { loaded ->
                    Column {
                        Spacer(modifier = Modifier.height(FruxSpacing.md))
                        ProfileContributionTiles(
                            items = contributionTiles(loaded, favoritesCount),
                            translucent = true,
                        )
                    }
                }
            }
        }
    }
}

/** Chevauchement des tuiles de compteurs sur le bas du bandeau (maquette #442). */
private val TILES_OVERLAP = 46.dp

/**
 * Remonte l'élément de [overlap] en rendant aussi cette hauteur à la colonne : un simple `offset`
 * ne déplace que le dessin et laisse sous les tuiles un vide de la même hauteur.
 */
private fun Modifier.overlapAbove(overlap: Dp): Modifier =
    layout { measurable, constraints ->
        val placeable = measurable.measure(constraints)
        val shift = overlap.roundToPx().coerceAtMost(placeable.height)
        layout(placeable.width, placeable.height - shift) { placeable.place(0, -shift) }
    }
